import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { useVendorSourceLookup } from "@hooks/shared/useSocketLookups";
import { buildPurchaseOrdersFilterSections, PURCHASE_ORDERS_DEFAULT_FILTERS } from "@config/filters/purchaseOrdersFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";

export const PURCHASE_ORDERS_FILTER_ACTIONS_ID = "purchase-orders-filter-actions";

/** Query params on the Purchase Orders URL that are not filters (sort + pagination are owned by the screen). */
const NON_FILTER_PARAMS = ["offset", "limit", "page", "order_by", "order_dir"];

type Props = {
    isSearching: boolean;
    /** Existing Purchase Orders fetch: receives the filter `a=b` parts; the screen appends sort and runs the socket call */
    searchFn: (filterParts: string[]) => void;
};

/**
 * Purchase Orders integration of the shared FilterBar (v2 search UI).
 * Screen key is ScreenOptionsEnum.PURCHASE_ORDERS ("purchase_orders"), which is also the presets JSON key.
 */
export const PurchaseOrdersFilterBar = (props: Props) => {
    const { searchFn } = props;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();
    const navigate = useNavigate();
    const vendorSourceLookup = useVendorSourceLookup();

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    const sections = useMemo(
        () => buildPurchaseOrdersFilterSections({ lookups: vendorSourceLookup, formatMessage }),
        [vendorSourceLookup, formatMessage]
    );

    // Deep links (e.g. /purchase_orders?po_state_id=2): read on mount and again on Back / Forward.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.PURCHASE_ORDERS);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            const parts = toQueryStringParts(query);

            // Keep the URL in sync so filtered views can be shared / deep-linked.
            // The mount search replaces (no extra history entry); user searches push.
            navigate(
                { pathname: "/purchase_orders", search: parts.length ? `?${parts.join("&")}` : "" },
                { replace: isFirstSearch.current }
            );
            isFirstSearch.current = false;

            searchFn(parts);
        },
        [navigate, searchFn, isFirstSearch]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.PURCHASE_ORDERS}
            mode={"drawer"}
            defaultFilters={PURCHASE_ORDERS_DEFAULT_FILTERS}
            initialFilters={initialFilters}
            allLabel={"all_purchase_orders"}
            actionsContainerId={PURCHASE_ORDERS_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default PurchaseOrdersFilterBar;
